import { DateTime } from 'luxon';

import { getUserFacingCategoryName } from '@core/constants/product';
import type { ProjectFormikType } from '@core/formik/project';
import Enums from '@core/types/api/Enums';
import type { Units } from '@core/types/units';
import { hasValue } from '@core/utils/equality';

export type ForecastExtra = {
  intervalStart: string;
  value: string;
  formikIdx: number;
  year: number;
  month: number;
};

export type DistributionMethod = 'monthly' | 'quarterly';

export type DistributionOptions = {
  /** Method for distributing volume across forecasts */
  distributionMethod?: DistributionMethod;
  /** Track which forecasts have been manually edited and should not be overwritten */
  manuallyEditedTracker?: Record<number, boolean>;
  /** Whether to respect manual edits (default: false for initial calculations) */
  respectManualEdits?: boolean;
  /** Whether to clear forecasts outside the project duration (default: true) */
  clearOutsideDuration?: boolean;
};

export type DistributionResult = {
  /** Updated forecasts array */
  forecasts: ProjectFormikType['forecasts'];
  /** Values that should be set via formik setFieldValue */
  valuesToSet?: Array<{ formikIdx: number; value: string }>;
};

/**
 * Core forecast distribution logic that can be used for both initial calculations
 * and interactive distribution in the forecasts section.
 */
export const distributeForecastVolume = (
  values: ProjectFormikType,
  forecastsExtra: ForecastExtra[],
  options: DistributionOptions = {},
): DistributionResult => {
  const {
    distributionMethod = 'monthly',
    manuallyEditedTracker = {},
    respectManualEdits = false,
    clearOutsideDuration = true,
  } = options;

  const allProducts = values.productsByPlants.flatMap((plant) => plant.products);

  // Only sum quantities from forecastable product categories
  // (Mix/Aggregate/Asphalt). Non-forecastable products (Resale, Precast,
  // Other, Charge) must not inflate the total that gets distributed into
  // forecast months. Callers that want to distribute a specific total (for
  // example a single-category sum, or a user override) should pass it via
  // `readyMixQuantityOverride`, which takes precedence below.
  const sumOfProductQuantities = allProducts.reduce((sum, product) => {
    if (!isForecastedProductCategory(product.product?.category)) {
      return sum;
    }
    const quantity = Number(product.quantity ?? 0);
    return sum + quantity;
  }, 0);

  const totalVolume = values.readyMixQuantityOverride ?? sumOfProductQuantities;

  if (!totalVolume || totalVolume <= 0) {
    return { forecasts: values.forecasts };
  }
  let startDate: DateTime;
  let endDate: DateTime;

  if (hasValue(values.estimatedStartDate) && hasValue(values.estimatedEndDate)) {
    startDate = DateTime.fromISO(values.estimatedStartDate);
    endDate = DateTime.fromISO(values.estimatedEndDate);

    if (!startDate.isValid || !endDate.isValid) {
      const now = DateTime.now();
      const isEarlyInMonth = now.day <= 15;
      startDate = isEarlyInMonth ? now.startOf('month') : now.plus({ months: 1 }).startOf('month');
      endDate = startDate.plus({ months: 11 }).endOf('month');
    }
  } else {
    const now = DateTime.now();
    const isEarlyInMonth = now.day <= 15;
    startDate = isEarlyInMonth ? now.startOf('month') : now.plus({ months: 1 }).startOf('month');
    endDate = startDate.plus({ months: 11 }).endOf('month');
  }

  const isForecastInProjectDuration = (forecast: ForecastExtra): boolean => {
    const fDate = DateTime.fromISO(forecast.intervalStart);
    return (
      fDate.startOf('month') >= startDate.startOf('month') &&
      fDate.startOf('month') <= endDate.startOf('month')
    );
  };

  const allActiveForecastsForDistribution = forecastsExtra.filter(isForecastInProjectDuration);

  if (allActiveForecastsForDistribution.length === 0) {
    return { forecasts: values.forecasts };
  }

  let remainingBudget = totalVolume;
  let nonManualForecasts = allActiveForecastsForDistribution;

  if (respectManualEdits) {
    const currentlyDistributed = allActiveForecastsForDistribution.reduce((sum, ff) => {
      if (manuallyEditedTracker[ff.formikIdx]) {
        const val = parseInt(ff.value, 10);
        return sum + (Number.isNaN(val) ? 0 : val);
      }
      return sum;
    }, 0);

    remainingBudget = Math.max(totalVolume - currentlyDistributed, 0);
    nonManualForecasts = allActiveForecastsForDistribution.filter(
      (ff) => !manuallyEditedTracker[ff.formikIdx],
    );
  }

  const valuesToSet: Array<{ formikIdx: number; value: string }> = [];

  if (nonManualForecasts.length > 0 && remainingBudget > 0) {
    if (distributionMethod === 'monthly') {
      const numActiveMonths = nonManualForecasts.length;
      const base = Math.floor(remainingBudget / numActiveMonths);
      const remainder = remainingBudget % numActiveMonths;

      nonManualForecasts.forEach((ff, idx) => {
        valuesToSet.push({
          formikIdx: ff.formikIdx,
          value: String(base + (idx < remainder ? 1 : 0)),
        });
      });
    } else {
      const quartersData: { [key: string]: { forecasts: typeof nonManualForecasts } } = {};

      nonManualForecasts.forEach((ff) => {
        const quarterIndex = Math.floor((ff.month - 1) / 3);
        const quarterKey = `${ff.year}-Q${quarterIndex}`;
        if (quartersData[quarterKey] === undefined) {
          quartersData[quarterKey] = { forecasts: [] };
        }
        quartersData[quarterKey].forecasts.push(ff);
      });

      const activeQuarters = Object.values(quartersData).filter((q) => q.forecasts.length > 0);
      if (activeQuarters.length > 0) {
        const baseVolumePerQuarter = Math.floor(remainingBudget / activeQuarters.length);
        const remainderVolumeForQuarters = remainingBudget % activeQuarters.length;

        activeQuarters.forEach((quarterInfo, qAllocIdx) => {
          const quarterVolume =
            baseVolumePerQuarter + (qAllocIdx < remainderVolumeForQuarters ? 1 : 0);
          const numMonthsInQuarter = quarterInfo.forecasts.length;

          if (numMonthsInQuarter > 0) {
            const basePerMonth = Math.floor(quarterVolume / numMonthsInQuarter);
            const remainderPerMonth = quarterVolume % numMonthsInQuarter;

            quarterInfo.forecasts.forEach((ff, mAllocIdx) => {
              valuesToSet.push({
                formikIdx: ff.formikIdx,
                value: String(basePerMonth + (mAllocIdx < remainderPerMonth ? 1 : 0)),
              });
            });
          }
        });
      }
    }
  }

  const activeFormikIndices = new Set(allActiveForecastsForDistribution.map((f) => f.formikIdx));
  const activeValues = new Map<number, string>();

  valuesToSet.forEach((item) => {
    activeValues.set(item.formikIdx, item.value);
  });

  const newForecasts = values.forecasts.map((f, index) => {
    const shouldClearValue =
      clearOutsideDuration &&
      !activeFormikIndices.has(index) &&
      (!respectManualEdits || !manuallyEditedTracker[index]);

    const shouldSetCalculatedValue =
      activeValues.has(index) && (!respectManualEdits || !manuallyEditedTracker[index]);

    if (shouldSetCalculatedValue) {
      return { ...f, value: activeValues.get(index) ?? '' };
    }
    if (shouldClearValue) {
      return { ...f, value: '' };
    }
    return f;
  });

  return {
    forecasts: newForecasts,
    valuesToSet: respectManualEdits ? valuesToSet : undefined,
  };
};

/**
 * Initial forecast calculation for new projects
 */
export const calculateInitialForecasts: (
  values: ProjectFormikType,
) => ProjectFormikType['forecasts'] = (values: ProjectFormikType) => {
  const forecastsExtra: ForecastExtra[] = values.forecasts.map((f, i) => {
    const dateTime = DateTime.fromISO(f.intervalStart);
    return {
      ...f,
      formikIdx: i,
      year: dateTime.year,
      month: dateTime.month,
    };
  });

  const result = distributeForecastVolume(values, forecastsExtra, {
    distributionMethod: 'monthly',
    respectManualEdits: false,
    clearOutsideDuration: true,
  });

  return result.forecasts;
};

export type ForecastValue = string | number | null | undefined;

export const toNum = (v: ForecastValue): number =>
  v !== '' && v !== null && v !== undefined ? Number(v) : 0;

export const isActive = (v: ForecastValue): boolean => v !== '' && v !== null && v !== undefined;

/**
 * Distributes the difference (Delta) between a target total and current sum across forecast months.
 *
 * **Distribution Strategy:**
 * 1. Calculates the `delta` needed to reach `newTotalVolume`.
 * 2. Identifies valid target months based on strict rules:
 * - Must be **Unlocked** (not in `lockedIndices`).
 * - Must be **Active** (value is not empty/null).
 * *Note: "0" is considered active; "" is considered inactive.*
 * 3. Distributes the delta evenly among targets, applying the remainder to the last target.
 *
 * @param currentForecasts - The current state of forecast months.
 * @param newTotalVolume - The desired total volume to match.
 * @param lockedIndices - Indices of months that must NOT be changed (e.g. user is typing).
 * @param productCategory - Optional category to filter forecasts. If provided, only affects forecasts of this category.
 */
export const distributeDeltaToForecasts = (
  currentForecasts: ProjectFormikType['forecasts'],
  newTotalVolume: number,
  lockedIndices: number[] = [],
  productCategory?: Enums.ProductCategory,
): ProjectFormikType['forecasts'] => {
  // Calculate current total only for the specified category (or all if not specified)
  const currentTotal = currentForecasts.reduce((sum, f) => {
    if (productCategory && f.productCategory !== productCategory) {
      return sum; // Skip forecasts from other categories
    }
    return sum + toNum(f.value);
  }, 0);
  const delta = newTotalVolume - currentTotal;

  if (Math.abs(delta) < 0.01) return currentForecasts;

  // Find targets only within the specified category (or all if not specified)
  const targets: number[] = [];
  currentForecasts.forEach((f, index) => {
    if (productCategory && f.productCategory !== productCategory) {
      return; // Skip forecasts from other categories
    }
    if (isActive(f.value) && !lockedIndices.includes(index)) {
      targets.push(index);
    }
  });

  if (targets.length === 0) return currentForecasts;

  const count = targets.length;
  const share = Math.floor(delta / count);
  const remainder = Math.round(delta - share * count);

  const nextForecasts = [...currentForecasts];

  targets.forEach((targetIndex, i) => {
    const isLast = i === count - 1;
    const adjustment = isLast ? share + remainder : share;
    const currentVal = toNum(nextForecasts[targetIndex].value);

    const newVal = Math.max(0, currentVal + adjustment);

    nextForecasts[targetIndex] = {
      ...nextForecasts[targetIndex],
      value: String(newVal),
    };
  });

  return nextForecasts;
};

/**
 * Helper function to extract product category from a product
 */
const getProductCategory = (
  product: ProjectFormikType['productsByPlants'][number]['products'][number],
): Enums.ProductCategory => {
  const directCategory = product.product?.category;
  if (hasValue(directCategory)) {
    return directCategory;
  }

  // No reliably-typed fallback available; default to Ready Mix.
  return Enums.ProductCategory.ReadyMix;
};

/**
 * The product categories that are included in project forecasts.
 *
 * Order matters: `getProductCategoriesInProject` returns results in this order
 * so that downstream forecast UIs display categories consistently.
 */
export const FORECASTED_PRODUCT_CATEGORIES: ReadonlyArray<Enums.ProductCategory> = [
  Enums.ProductCategory.ReadyMix,
  Enums.ProductCategory.Aggregate,
  Enums.ProductCategory.Asphalt,
];

/**
 * Forecasted categories that have data, in `FORECASTED_PRODUCT_CATEGORIES` order. Empty until
 * every probe is fetched, so the default doesn't depend on which request resolves first.
 */
export const getProbedForecastCategories = (
  probes: { category: Enums.ProductCategory; isFetched: boolean; hasData: boolean }[],
): Enums.ProductCategory[] => {
  if (!probes.every((probe) => probe.isFetched)) return [];

  return FORECASTED_PRODUCT_CATEGORIES.filter((category) =>
    probes.some((probe) => probe.category === category && probe.hasData),
  );
};

/**
 * Returns true when the given product category is one that participates in
 * project forecasts (Mix, Aggregate, or Asphalt). Returns false for all other
 * categories (Resale, Precast, Other, Charge) as well as null/undefined.
 */
export const isForecastedProductCategory = (
  category: Enums.ProductCategory | null | undefined,
): boolean =>
  category !== null && category !== undefined && FORECASTED_PRODUCT_CATEGORIES.includes(category);

/**
 * Get unique product categories present in a project's products.
 *
 * Only returns categories from `FORECASTED_PRODUCT_CATEGORIES` — other
 * categories (e.g. Resale, Precast, Other, Charge) do not participate in
 * project forecasts and are filtered out here.
 */
export const getProductCategoriesInProject = (
  productsByPlants: ProjectFormikType['productsByPlants'],
): Enums.ProductCategory[] => {
  const categorySet = new Set<Enums.ProductCategory>();

  productsByPlants.forEach((plant) => {
    plant.products.forEach((product) => {
      const category = getProductCategory(product);
      if (category) {
        categorySet.add(category);
      }
    });
  });

  // Return only forecasted categories (Mix, Aggregate, Asphalt) in consistent
  // order. Filter out "Other" and any miscellaneous categories.
  return FORECASTED_PRODUCT_CATEGORIES.filter((category) => categorySet.has(category));
};

/**
 * Get default unit for a product category based on tenant configuration
 */
export const getDefaultUnitForCategory = (
  category: Enums.ProductCategory,
  tenantUnits: Units,
): Enums.Unit => {
  switch (category) {
    case Enums.ProductCategory.ReadyMix:
      return tenantUnits.dryVolume;
    case Enums.ProductCategory.Aggregate:
    case Enums.ProductCategory.Asphalt:
      return tenantUnits.weight;
    default:
      return tenantUnits.dryVolume;
  }
};

/**
 * Forecastable product categories ordered alphabetically by their user-facing
 * name (Aggregates, Asphalt, Ready Mix), for user-facing copy.
 */
export const FORECASTED_PRODUCT_CATEGORIES_ALPHABETICAL: ReadonlyArray<Enums.ProductCategory> = [
  Enums.ProductCategory.Aggregate, // "Aggregates"
  Enums.ProductCategory.Asphalt, // "Asphalt"
  Enums.ProductCategory.ReadyMix, // "Ready Mix"
];

/**
 * Comma-separated list of forecastable product categories by their user-facing
 * name in alphabetical order ("Aggregates, Asphalt, Ready Mix"). Use in any
 * user-visible messaging so copy stays in sync with the selector labels.
 */
export const forecastableCategoryDisplayList = (): string =>
  FORECASTED_PRODUCT_CATEGORIES_ALPHABETICAL.map(getUserFacingCategoryName).join(', ');

/**
 * Sum product quantities for a specific category
 */
export const sumProductQuantitiesByCategory = (
  productsByPlants: ProjectFormikType['productsByPlants'],
  category: Enums.ProductCategory,
): number => {
  const allProducts = productsByPlants.flatMap((plant) => plant.products);

  return allProducts.reduce((sum, product) => {
    const productCategory = getProductCategory(product);
    if (productCategory === category) {
      const quantity = Number(product.quantity);
      return sum + (Number.isNaN(quantity) ? 0 : quantity);
    }
    return sum;
  }, 0);
};
